import { useEffect, useState } from "react";
import { tv } from "tailwind-variants";
import { t } from "i18next";
import {
  faCheckCircle,
  faExclamationTriangle,
  faSpinner,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faShopify } from "@fortawesome/free-brands-svg-icons";

import formatError from "@/utils/formatError";
import getStartedImage from "@/assets/images/collect/get-started.png";
import { useShopifyStoreDetails } from "@/requests/hooks/collect";

import { Caption, H5, HeaderTextBlock, P } from "@/components/Typography";
import { SplitLayout } from "@/layouts";
import Shimmer from "@/components/Shimmer";
import { Modal } from "@/components/Modal";
import { SignUpForm } from "@/components/Form";
import { getAppConfig } from "@/utils/appManager";

const styles = tv({
  base: "h-50 w-50",
  variants: {
    isError: { true: "text-red", false: "text-secondary" },
  },
  defaultVariants: { isError: false },
});

const Signup = () => {
  const {
    data: storeDetails,
    isFetching: isFetchingShopifyStoreDetails,
    isSuccess: isShopifyStoreDetailsSuccess,
    error: storeDetailsError,
  } = useShopifyStoreDetails();
  const { shopifyAppInstallUrl } = getAppConfig();

  const [modalOpen, setModalOpen] = useState<boolean>(true);
  const [modalMessage, setModalMessage] = useState<string>("");
  const [isIntegrationError, setIntegrationError] = useState<boolean>(false);

  const toggleModal = () => setModalOpen(modalOpen => !modalOpen);

  const handleSendToShopify = () => {
    if (!modalOpen) toggleModal();
    setTimeout(() => {
      window.location.replace(shopifyAppInstallUrl);
    }, 3000);
  };

  const handleIntegrationInstallationError = () => setIntegrationError(true);

  useEffect(() => {
    if (!storeDetailsError || !!storeDetails?.shopUrl) return;

    const { codes } = formatError(storeDetailsError);

    if (codes?.includes("INTEGRATION_INSTALLATION_NOT_FOUND")) {
      handleSendToShopify();
    }
  }, [storeDetailsError, storeDetails]);

  useEffect(() => {
    if (isFetchingShopifyStoreDetails) {
      setModalMessage(t("emailOnboarding.signup.modal.fetching"));
    } else if (isIntegrationError) {
      setModalMessage(t("emailOnboarding.signup.modal.failed"));
      handleSendToShopify();
    } else if (isShopifyStoreDetailsSuccess) {
      toggleModal();
    }
  }, [
    isIntegrationError,
    isFetchingShopifyStoreDetails,
    isShopifyStoreDetailsSuccess,
  ]);

  const renderChecklist = () => {
    const checkListItems = [
      "item1" as const,
      "item2" as const,
      "item3" as const,
    ];

    return (
      <div className="mt-30">
        <H5 color="white" className="font-semibold">
          {t("emailOnboarding.signup.left.getStarted.title")}
        </H5>
        <div
          data-testid="signup-checklist-items"
          className="mt-30 flex flex-col items-start justify-center gap-30">
          {checkListItems.map((checkListItem, i) => (
            <div
              className="flex flex-row items-start gap-10 text-white md:gap-20"
              key={i}>
              <FontAwesomeIcon
                icon={faCheckCircle}
                className="mt-[5px] h-20 w-20 text-white md:mt-[unset] md:h-30 md:w-30"
              />
              {t(`emailOnboarding.signup.left.getStarted.${checkListItem}`)}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center">
          <img
            className="mt-50 hidden h-auto max-w-full text-center md:flex md:max-w-[420px]"
            src={getStartedImage}
          />
        </div>
      </div>
    );
  };

  const renderShopifyStoreName = () => {
    if (isFetchingShopifyStoreDetails)
      return (
        <div className="mt-20">
          <Shimmer width="100%" height={61} rounded="sm" />
        </div>
      );

    return (
      <div
        data-testid="store-name-display"
        className="mt-20 flex flex-row items-center justify-start gap-10 rounded-sm bg-primary-light p-10">
        <div className="flex h-35 w-35 items-center justify-center rounded-full bg-gradient-to-r from-primary to-green">
          <FontAwesomeIcon
            icon={faShopify}
            className="h-25 w-auto text-white"
          />
        </div>
        <div className="flex flex-col">
          <div className="mb-0">
            <Caption color="secondary">
              {t("emailOnboarding.signup.right.inputLabel")}
            </Caption>
          </div>
          <div className="font-semibold text-primary-dark">
            {storeDetails?.shopName}
          </div>
        </div>
      </div>
    );
  };

  const renderLeftPanel = () => {
    return (
      <div className="md:mr-50">
        <HeaderTextBlock
          align="left"
          featureText={t("emailOnboarding.signup.left.header.feature")}
          headingText={t("emailOnboarding.signup.left.header.heading")}
          headingTextColor="white"
          descriptionText={t("emailOnboarding.signup.left.header.description")}
          descriptionTextColor="white"
        />
        {renderChecklist()}
      </div>
    );
  };

  const renderRightPanel = () => {
    return (
      <div className="mb-40 max-w-400 md:mb-[unset] md:ml-70">
        <HeaderTextBlock
          align="left"
          featureText={t("emailOnboarding.signup.right.header.feature")}
          headingText={t("emailOnboarding.signup.right.header.heading")}
        />
        {renderShopifyStoreName()}
        <SignUpForm
          onboardingFlow="MARKETING"
          handleIntegrationInstallationError={
            handleIntegrationInstallationError
          }
        />
      </div>
    );
  };

  const renderModal = () => (
    <Modal
      isOpen={modalOpen}
      toggleIsOpen={toggleModal}
      align="center"
      hideCloseButton={true}
      testId="fetch-store-info-modal">
      <div className="flex flex-col items-center justify-center gap-30 text-center">
        <FontAwesomeIcon
          icon={
            isFetchingShopifyStoreDetails ? faSpinner : faExclamationTriangle
          }
          className={styles({ isError: !isFetchingShopifyStoreDetails })}
          spin={isFetchingShopifyStoreDetails}
          beatFade={!isFetchingShopifyStoreDetails}
        />
        <P>{modalMessage}</P>
      </div>
    </Modal>
  );

  return (
    <>
      <SplitLayout
        isDark={true}
        leftPanel={renderLeftPanel()}
        rightPanel={renderRightPanel()}
        backgroundSplit="leftSkewed"
      />
      {renderModal()}
    </>
  );
};

export default Signup;
